import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { X, Plus, Trash2, FileText, Dumbbell, Clock, Search, ArrowUp, ArrowDown, UserPlus, Eye } from 'lucide-react';
import LoadingSpinner from '../../common/LoadingSpinner';
import CreateTemplateModal from './CreateTemplateModal';
import AssignTemplateModal from './AssignTemplateModal';
import TemplatePreviewModal from './TemplatePreviewModal';
import IOSModal from '../../ui/IOSModal';

export default function GroupDetailsModal({ isOpen, group, onClose, onUpdate }) {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();

  const [groupDetails, setGroupDetails] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddTemplates, setShowAddTemplates] = useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTemplateForAssign, setSelectedTemplateForAssign] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [modalOrigin, setModalOrigin] = useState({ x: 0, y: 0 });
  const createTemplateButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen && group) {
      fetchGroupDetails();
      fetchAllTemplates();
    }
  }, [isOpen, group]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/gym_template_groups.php?action=get&id=${group.id}`);
      
      if (response.data && response.data.id) {
        setGroupDetails(response.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento dettagli gruppo:', error);
      toast.error('Errore nel caricamento dei dettagli del gruppo');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTemplates = async () => {
    try {
      const response = await api.get('/gym_workout_templates.php?action=list');
      if (Array.isArray(response.data)) {
        setAllTemplates(response.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento template:', error);
    }
  };

  const handleAddTemplate = async (template) => {
    try {
      const response = await api.post('/gym_template_groups.php?action=add_template', {
        group_id: group.id,
        template_id: template.id,
        display_order: groupDetails?.templates?.length || 0
      });

      if (response.data.success) {
        toast.success('Template aggiunto al gruppo!');
        fetchGroupDetails();
        onUpdate();
      }
    } catch (error) {
      console.error('Errore nell\'aggiunta template:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'aggiunta del template');
    }
  };

  const handlePreviewTemplate = async (template) => {
    try {
      setPreviewLoading(true);
      const response = await api.get(`/gym_workout_templates.php?action=get&id=${template.id}`);
      if (response.data && response.data.id) {
        setPreviewTemplate(response.data);
        setShowPreviewModal(true);
      } else {
        toast.error('Impossibile caricare il template');
      }
    } catch (error) {
      console.error('Errore anteprima template:', error);
      toast.error('Errore nel caricamento dell\'anteprima');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRemoveTemplate = async (template) => {
    if (!window.confirm(`Rimuovere il template "${template.name}" dal gruppo?`)) {
      return;
    }

    try {
      const response = await api.delete(
        `/gym_template_groups.php?action=remove_template&group_id=${group.id}&template_id=${template.id}`
      );

      if (response.data.success) {
        toast.success('Template rimosso dal gruppo!');
        fetchGroupDetails();
        onUpdate();
      }
    } catch (error) {
      console.error('Errore nella rimozione template:', error);
      toast.error(error.response?.data?.error || 'Errore nella rimozione del template');
    }
  };

  // Template già nel gruppo
  const templatesInGroup = groupDetails?.templates || [];
  const templateIdsInGroup = new Set(templatesInGroup.map(t => t.id));

  // Template disponibili da aggiungere
  const availableTemplates = allTemplates.filter(t => !templateIdsInGroup.has(t.id));

  // Filtra template disponibili
  const filteredAvailableTemplates = availableTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  if (!isOpen || !group) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {groupDetails?.name || group.name}
              </h2>
              {groupDetails?.description && (
                <p className="text-sm text-gray-600 dark:text-white/65 mt-1">
                  {groupDetails.description}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Template nel gruppo */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Template nel Gruppo ({templatesInGroup.length})
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      ref={createTemplateButtonRef}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setModalOrigin({
                          x: rect.left + rect.width / 2,
                          y: rect.top + rect.height / 2
                        });
                        setShowCreateTemplateModal(true);
                      }}
                      className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                      title="Crea un nuovo template e aggiungilo al gruppo"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Crea Template
                    </button>
                    <button
                      onClick={() => setShowAddTemplates(!showAddTemplates)}
                      className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {showAddTemplates ? 'Chiudi' : 'Aggiungi Template'}
                    </button>
                  </div>
                </div>

                {templatesInGroup.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-white/65">
                      Nessun template nel gruppo. Aggiungi template per iniziare.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templatesInGroup.map((template, index) => (
                      <TemplateCardInGroup
                        key={template.id}
                        template={template}
                        index={index}
                        previewDisabled={previewLoading}
                        onPreview={() => handlePreviewTemplate(template)}
                        onRemove={() => handleRemoveTemplate(template)}
                        onAssign={() => {
                          setSelectedTemplateForAssign(template);
                          setShowAssignModal(true);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Aggiungi template */}
              {showAddTemplates && (
                <div className="border-t border-gray-200 dark:border-white/10 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Template Disponibili ({filteredAvailableTemplates.length})
                  </h3>

                  {/* Ricerca */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Cerca template..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {filteredAvailableTemplates.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-white/65">
                        {searchQuery ? 'Nessun template trovato' : 'Tutti i template sono già nel gruppo'}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredAvailableTemplates.map(template => (
                        <TemplateCardAvailable
                          key={template.id}
                          template={template}
                          onAdd={() => handleAddTemplate(template)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modale Crea Template - A pieno schermo */}
      <IOSModal
        isOpen={showCreateTemplateModal}
        onClose={() => setShowCreateTemplateModal(false)}
        origin={modalOrigin}
        size="full"
        title="Nuovo Template"
        className="
          !w-[90vw]
          !h-[90vh]
          !max-w-[90vw]
          !max-h-[90vh]
        "
      >
        {showCreateTemplateModal && (
          <CreateTemplateModal
            isOpen={showCreateTemplateModal}
            onClose={() => setShowCreateTemplateModal(false)}
            onSuccess={(newTemplate) => {
              // Il template è già stato aggiunto al gruppo automaticamente dal CreateTemplateModal
              // Ricarica i dettagli del gruppo per aggiornare la lista
              fetchGroupDetails();
              onUpdate();
              setShowCreateTemplateModal(false);
            }}
            groupId={group.id}
            hideHeader={true}
          />
        )}
      </IOSModal>

      {showPreviewModal && previewTemplate && (
        <TemplatePreviewModal
          isOpen={showPreviewModal}
          template={previewTemplate}
          overlayClassName="z-[100]"
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewTemplate(null);
          }}
        />
      )}

      {/* Modale Assegna Template */}
      {showAssignModal && selectedTemplateForAssign && (
        <AssignTemplateModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedTemplateForAssign(null);
          }}
          template={selectedTemplateForAssign}
          onSuccess={() => {
            toast.success('Template assegnato con successo!');
            setShowAssignModal(false);
            setSelectedTemplateForAssign(null);
          }}
        />
      )}
    </div>
  );
}

// Template Card nel gruppo
function TemplateCardInGroup({ template, index, onPreview, onRemove, onAssign, previewDisabled }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-white/10 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
              #{index + 1}
            </span>
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {template.name}
            </h4>
          </div>
          {template.description && template.description.trim() !== '' && (
            <p className="text-sm text-gray-600 dark:text-white/65 line-clamp-2 mb-2">
              {template.description}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-white/65 mb-3">
        <span className="flex items-center">
          <Dumbbell className="h-4 w-4 mr-1" />
          {template.exercise_count || 0} es.
        </span>
        {template.estimated_duration && (
          <span className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            {template.estimated_duration} min
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={onPreview}
        disabled={previewDisabled}
        className="w-full mb-2 flex items-center justify-center px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg transition-colors disabled:opacity-50"
      >
        <Eye className="h-4 w-4 mr-1" />
        Anteprima esercizi
      </button>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onAssign}
          className="flex items-center justify-center px-3 py-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
        >
          <UserPlus className="h-4 w-4 mr-1" />
          Assegna
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="flex items-center justify-center px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Rimuovi
        </button>
      </div>
    </div>
  );
}

// Template Card disponibile
function TemplateCardAvailable({ template, onAdd }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-white/10 p-4 hover:shadow-md transition-shadow">
      <div className="mb-3">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
          {template.name}
        </h4>
        {template.description && template.description.trim() !== '' && (
          <p className="text-sm text-gray-600 dark:text-white/65 line-clamp-2 mb-2">
            {template.description}
          </p>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-white/65 mb-3">
        <span className="flex items-center">
          <Dumbbell className="h-4 w-4 mr-1" />
          {template.exercise_count || 0} es.
        </span>
        {template.estimated_duration && (
          <span className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            {template.estimated_duration} min
          </span>
        )}
      </div>

      <button
        onClick={onAdd}
        className="w-full flex items-center justify-center px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
      >
        <Plus className="h-4 w-4 mr-1" />
        Aggiungi
      </button>
    </div>
  );
}

